import { UUID, uuidv7 } from "uuidv7"

import { addUserToBuf } from "~/utils/flatbuffers/user"

import { apiIdFor, hasGroup, orderedId, PropertyGroup, PropertyId, PropertySchema, PropertyStatus } from "../entity"
import { BaseEntity } from "../entity/BaseEntity"

export abstract class BaseUser extends BaseEntity<UserId, UserProperty, User> {
  static clientPreHash(): UserId {
    return `00010000-${uuidv7()}` as UserId
  }

  static generateClientOnlyUserId(): UserId {
    return `00010003-${uuidv7()}` as UserId
  }

  override toNavigationUrl(): string {
    throw new Error("Method not implemented.")
  }

  static override async idFromPrehash<I = UserId>(preHash: string): Promise<I> {
    return super.idFromPrehash(preHash) as I
  }

  addToBuf(builder: DatagramBufferBuilder) {
    return addUserToBuf(builder, this.inner())
  }

  type() {
    return apiIdFor(this.id)
  }

  email() {
    const property = this.property(PropertyId.USER_EMAIL)
    if (!property)
      return
    if (property.value.type === PropertySchema.EmailValue)
      return property.value.email
    console.error("Invalid schema", property.value)
    throw new Error("`Schema invalid for email")
  }

  handle(): EntityHandle | undefined {
    const property = this.property(PropertyId.USER_HANDLE)
    if (!property)
      return
    if (property.value.type === PropertySchema.EntityHandleValue)
      return property.value.entityHandle
    console.error("Invalid schema", property.value)
    throw new Error("`Schema invalid for handle")
  }

  /**
   * Optional as user might not have this property. You almost certainly
   * want to use the computed value in useSelf instead
   */
  interfaceSettings() {
    const property = this.property(PropertyId.USER_INTERFACE_SETTINGS)
    if (!property)
      return
    if (property.value.type === PropertySchema.UserInterfaceSettingsValue)
      return property.value.userInterfaceSettings
    console.error("Invalid schema", property.value)
    throw new Error("`Schema invalid for interface settings")
  }

  static async sidebarIndexPropertyId(fractionalIndex: string) {
    const encoder = new TextEncoder()
    const data = encoder.encode(fractionalIndex)
    const hash = await window.crypto.subtle.digest("SHA-256", data)
    const uuid = UUID.ofInner(new Uint8Array(hash.slice(0, 16)))
    const groupStr = PropertyGroup.UserSidebarOrderedItemMetaProperty.toString(16).padStart(8, "0")
    return `${groupStr}-${uuid.toString()}` as PropertyId
  }

  static async createPropertyForSidebarFile({ userId, fileId, fractionalIndex, timestamp }: { userId: UserId, fileId: FileId, fractionalIndex: string, timestamp: string }): Promise<UserProperty> {
    return {
      id: userId,
      propertyId: await BaseUser.sidebarIndexPropertyId(fractionalIndex),
      schema: PropertySchema.UserSidebarItemIdValue,
      version: 0,
      value: { type: PropertySchema.UserSidebarItemIdValue, UserSidebarItemId: { fractionalIndex, fileItemId: fileId } },
      status: PropertyStatus.StatusPending,
      timestamp,
    }
  }

  static createPropertyForSidebarSearch({ searchId, userId, fractionalIndex, timestamp }: { userId: UserId, searchId: SearchId, fractionalIndex: string, timestamp: string }): UserProperty {
    return {
      id: userId,
      propertyId: orderedId(PropertyGroup.UserSidebarOrderedItemMetaProperty, 0),
      schema: PropertySchema.UserSidebarItemIdValue,
      version: 0,
      value: { type: PropertySchema.UserSidebarItemIdValue, UserSidebarItemId: { fractionalIndex, searchItemId: searchId } },
      status: PropertyStatus.StatusPending,
      timestamp,
    }
  }

  static createPropertyForSidebarConversation({ conversationId, userId, fractionalIndex, timestamp }: { userId: UserId, conversationId: ConversationId, fractionalIndex: string, timestamp: string }): UserProperty {
    return {
      id: userId,
      propertyId: orderedId(PropertyGroup.UserSidebarOrderedItemMetaProperty, 0),
      schema: PropertySchema.UserSidebarItemIdValue,
      version: 0,
      value: { type: PropertySchema.UserSidebarItemIdValue, UserSidebarItemId: { fractionalIndex, conversationItemId: conversationId } },
      status: PropertyStatus.StatusPending,
      timestamp,
    }
  }

  /** Returns all of the user object's sidebar entries */
  sidebarEntryProperties() {
    const properties: UserProperty[] = []
    for (const property of Object.values(this.properties)) {
      if (!property)
        continue
      if (!hasGroup(property.propertyId as PropertyId, PropertyGroup.UserSidebarOrderedItemMetaProperty))
        continue
      properties.push(property)
    }
    return properties
  }
}
